import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import localStore from '@shared/utils/localStore.ts';

import type { StudentMeDto, StudentTokensDto } from '../services/studentAuthApi.ts';

interface StudentAuthState {
  tokens?: { accessToken: string | null; refreshToken: string | null };
  isAuthenticated: boolean;
  studentAccountId?: string;
  student?: StudentMeDto;
}

interface RootStateWithStudentAuth {
  studentAuth: StudentAuthState;
  [key: string]: unknown;
}

const initialState: StudentAuthState = {
  tokens: localStore.getStudentTokens(),
  isAuthenticated: localStore.getStudentTokens().accessToken !== null,
  studentAccountId: localStore.getStudentAccountId(),
  student: undefined,
};

export const studentAuthSlice = createSlice({
  name: 'studentAuth',
  initialState,
  reducers: {
    studentLogin: (state, action: PayloadAction<StudentTokensDto>) => {
      const { accessToken, refreshToken, studentAccountId } = action.payload;
      localStore.setStudentTokens({ accessToken, refreshToken });
      localStore.setStudentAccountId(studentAccountId);
      state.tokens = { accessToken, refreshToken };
      state.studentAccountId = studentAccountId;
      state.isAuthenticated = true;
    },
    studentLogout: state => {
      state.tokens = undefined;
      state.isAuthenticated = false;
      state.studentAccountId = undefined;
      state.student = undefined;
      localStore.clearStudentTokens();
      localStore.deleteStudentAccountId();
    },
    studentUpdateTokens: (state, action: PayloadAction<StudentTokensDto>) => {
      const { accessToken, refreshToken, studentAccountId } = action.payload;
      localStore.setStudentTokens({ accessToken, refreshToken });
      localStore.setStudentAccountId(studentAccountId);
      state.tokens = { accessToken, refreshToken };
      state.studentAccountId = studentAccountId;
    },
    setStudentProfile: (state, action: PayloadAction<StudentMeDto>) => {
      state.student = action.payload;
      state.studentAccountId = action.payload.id;
    },
  },
});

export const { studentLogin, studentLogout, studentUpdateTokens, setStudentProfile } =
  studentAuthSlice.actions;

export const selectIsStudentAuthenticated = (state: RootStateWithStudentAuth) =>
  state.studentAuth.isAuthenticated;

export const selectStudentProfile = (state: RootStateWithStudentAuth) => state.studentAuth.student;

export default studentAuthSlice.reducer;
